import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useLocation,
} from "react-router-dom";

import Cursor from "./components/Cursor";
import { motion } from "framer-motion";
import WaitlistForm from "./components/Waitlist.js";
import HomeShowcase from "./components/HomeShowcase.jsx";
import PresaleBand from "./components/PresaleBand";
import StartHere from "./components/StartHere.jsx";
import MouseFooter from "./components/MouseFooter";
import HeroVideo from "./components/HeroVideo.jsx";
import Privacy from "./pages/Privacy.js";
import Terms from "./pages/Terms.js";
import Devices from "./pages/Devices.js";
import Blog from "./pages/Blog.js";
import Download from "./pages/Download.js";
import Docs from "./pages/Docs";
import Egoscan from "./pages/Explorer";
import Claim from "./pages/Claim";
import GrantAdmin from "./pages/GrantAdmin";
import PresaleSuccess from "./pages/PresaleSuccess";
import PresaleCancel from "./pages/PresaleCancel";
import { Navigate } from "react-router-dom";
import TeamSection from "./components/TeamSection.jsx";

// -----------------------------
// Wrapper with Router
// -----------------------------
export default function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

// -----------------------------
// Actual app content
// -----------------------------
function AppContent() {
  const [menuOpen, setMenuOpen] = useState(false);

  const location = useLocation();

  // 👇 Close side menu whenever route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location]);

  const handleScroll = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -70;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    const navbar = document.querySelector(".navbar");
    const onScroll = () => {
      if (window.scrollY > 50) {
        navbar.style.background = "rgba(10, 10, 10, 0.95)";
        navbar.style.backdropFilter = "blur(8px)";
      } else {
        navbar.style.background = "rgba(20, 20, 20, 0.2)";
        navbar.style.backdropFilter = "none";
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="app">
      <Cursor />
      {/* 
        Please move these styles to your main CSS file (e.g., hero.css) 
        I've added them here for demonstration purposes.
      */}
      <style>{`
        /* Web3 Ambient Background Glow */
        .web3-bg-glow {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            overflow: hidden;
            z-index: -5;
            pointer-events: none;
        }
        .web3-orb {
            position: absolute;
            width: 70vw; height: 70vw;
            background: radial-gradient(circle, rgba(255,255,255,0.03) 0%, transparent 60%);
            top: -20%; left: -10%;
            animation: float 25s infinite alternate ease-in-out;
            border-radius: 50%;
        }
        .web3-orb.two {
            top: 40%; left: 50%;
            animation-delay: -5s;
            background: radial-gradient(circle, rgba(200,200,255,0.02) 0%, transparent 60%);
        }
        @keyframes float {
            0% { transform: translate(0, 0) scale(1); }
            100% { transform: translate(10vw, 10vh) scale(1.1); }
        }

        /* ---- Hero status badge ---- */
        .ego-badge {
            display: inline-flex; align-items: center; gap: 0.55rem;
            padding: 0.45rem 0.95rem;
            border: 1px solid rgba(159, 216, 255, 0.35);
            border-radius: 999px;
            font-size: 0.72rem; letter-spacing: 0.18em; text-transform: uppercase;
            color: #cfe9ff; background: rgba(159, 216, 255, 0.06);
            backdrop-filter: blur(6px);
        }
        .ego-badge .dot {
            width: 7px; height: 7px; border-radius: 50%;
            background: #9FD8FF; box-shadow: 0 0 0 0 rgba(159,216,255,0.6);
            animation: livedot 2.2s infinite;
        }
        @keyframes livedot {
            0% { box-shadow: 0 0 0 0 rgba(159,216,255,0.5); }
            70% { box-shadow: 0 0 0 9px rgba(159,216,255,0); }
            100% { box-shadow: 0 0 0 0 rgba(159,216,255,0); }
        }
        .hero-copy {
            position: absolute; left: 0; right: 0; bottom: 8%;
            display: flex; flex-direction: column; align-items: center;
            gap: 1.4rem; padding: 0 1.5rem; text-align: center;
        }
        .hero-tagline {
            color: #c9ccd1; max-width: 40ch; font-size: clamp(0.95rem, 2vw, 1.15rem);
            line-height: 1.55; margin: 0;
        }
        .hero-tagline b { color: #fff; font-weight: 600; }

        /* ---- Now / Next roadmap ---- */
        .roadmap { padding: 4rem 1.5rem 6rem; max-width: 920px; margin: 0 auto; }
        .roadmap-grid {
            display: grid; grid-template-columns: 1fr 1fr; gap: 1.4rem;
        }
        @media (max-width: 720px) { .roadmap-grid { grid-template-columns: 1fr; } }
        .road-col {
            border: 1px solid rgba(255,255,255,0.08); border-radius: 16px;
            padding: 2rem 1.8rem; background: rgba(16,17,22,0.55);
        }
        .road-col.next { border-style: dashed; opacity: 0.96; }
        .road-tag {
            font-size: 0.7rem; letter-spacing: 0.28em; text-transform: uppercase;
            margin-bottom: 1.2rem;
        }
        .road-col.now .road-tag { color: #9FD8FF; }
        .road-col.next .road-tag { color: #8a929c; }
        .road-col h3 { font-size: 1.3rem; margin: 0 0 1rem; }
        .road-col ul { list-style: none; padding: 0; margin: 0; }
        .road-col li {
            display: flex; gap: 0.7rem; padding: 0.5rem 0;
            color: #b9bdc4; line-height: 1.45; font-size: 0.95rem;
        }
        .road-col li::before { content: ""; flex: none; margin-top: 0.5rem; width: 6px; height: 6px; border-radius: 50%; background: #9FD8FF; }
        .road-col.next li::before { background: #555b63; }

        /* ---- Respect reduced motion on the new animated section ---- */
        @media (prefers-reduced-motion: reduce) {
            .web3-orb { animation: none; }
            .ego-badge .dot { animation: none; }
        }
      `}</style>

      {/* Deep Space / Web3 Ambient Background */}
      <div className="web3-bg-glow">
        <div className="web3-orb"></div>
        <div className="web3-orb two"></div>
      </div>

      {/* Navbar */}
      <header className="navbar">
        <div className="logo">
          <Link
            to="/"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <img src="/ego.png" alt="Ego Blockchain Logo" className="logo-img" />
          </Link>
        </div>

        {/* Hamburger button */}
        <button
          className={`hamburger ${menuOpen ? "active" : ""}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Nav links */}
        <nav className={`nav-links ${menuOpen ? "open" : ""}`}>
          <Link
            to="/download"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Download Ego Desktop
          </Link>

          <a
            href="https://chromewebstore.google.com/detail/jpifpbadfhhfapcgkilnmlpfjjdjakja"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ego Wallet for Chrome
          </a>

          <Link
            to="/blog"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Blog
          </Link>

          <Link
            to="/docs"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Docs
          </Link>

          <Link
            to="/egoscan"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            EgoScan
          </Link>

          <Link
            to="/claim"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Claim EGOC
          </Link>

        </nav>
      </header>

      {/* Overlay (click to close) */}
      {menuOpen && (
        <div className="menu-overlay" onClick={() => setMenuOpen(false)} />
      )}

      {/* Routes */}
      <Routes>
        <Route
          path="/"
          element={
            <>
              {/* Hero — looping video fills viewport, copy + buttons at bottom */}
              <section
                style={{
                  position: "relative",
                  height: "100vh",
                  overflow: "hidden",
                  background: "#000",
                }}
              >
                <HeroVideo />
                {/* gradient only at bottom so image text stays readable */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: "55%",
                    background:
                      "linear-gradient(to top, rgba(0,0,0,0.9) 0%, transparent 100%)",
                  }}
                />

                {/* Hero copy + CTAs anchored to bottom */}
                <motion.div
                  className="hero-copy"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                >
                  <span className="ego-badge">
                    <span className="dot" /> Ego Desktop — Available Now
                  </span>

                  <p className="hero-tagline">
                    <b>Your computer can earn from the space and power it is not using.</b>{" "}
                    No coding, no special hardware, no crypto experience needed.
                  </p>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "1rem",
                      flexWrap: "wrap",
                    }}
                  >
                    <a
                      href="#start-here"
                      className="btn"
                      onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("start-here")?.scrollIntoView({ behavior: "smooth" });
                      }}
                    >
                      <button>How it works</button>
                    </a>
                    <Link
                      to="/download"
                      className="btn secondary"
                      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    >
                      <button className="secondary">Download Ego Desktop</button>
                    </Link>
                  </div>
                </motion.div>
              </section>

              <StartHere />

              <HomeShowcase />

              <PresaleBand />

              {/* Now / Next roadmap */}
              <section className="roadmap">
                <div className="roadmap-grid">
                  <motion.div
                    className="road-col now"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    viewport={{ once: false, amount: 0.3 }}
                  >
                    <div className="road-tag">● Now</div>
                    <h3>Ego Desktop</h3>
                    <ul>
                      <li>Rent or earn GPU &amp; CPU compute, settled in EGOC</li>
                      <li>Host apps and services on the network</li>
                      <li>Shielded, private EGOC transactions</li>
                      <li>Build and deploy dApps, with EgoScan and an AI Workspace</li>
                    </ul>
                  </motion.div>

                  <motion.div
                    className="road-col next"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                    viewport={{ once: false, amount: 0.3 }}
                  >
                    <div className="road-tag">○ Next</div>
                    <h3>5G-Native Devices</h3>
                    <ul>
                      <li>Certified Ego devices optimized for 5G, IoT and edge</li>
                      <li>Feeless, mobile-first node operation</li>
                      <li>Verifiable coverage and storage proofs (PoC, PoRep, PoSt)</li>
                      <li>Post-quantum cryptography and sharded BFT</li>
                    </ul>
                  </motion.div>
                </div>
              </section>

              {/* Team Section */}
              <TeamSection />

              <WaitlistForm />
              <MouseFooter handleScroll={handleScroll} />
            </>
          }
        />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/devices" element={<Devices />} />
        <Route path="/download" element={<Download />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<Blog />} />
        <Route path="/docs" element={<Docs />} />
        <Route path="/egoscan" element={<Egoscan />} />
        <Route path="/claim" element={<Claim />} />
        <Route path="/success" element={<PresaleSuccess />} />
        <Route path="/cancel" element={<PresaleCancel />} />
        <Route path="/grant" element={<Navigate to="/claim" replace />} />
        <Route path="/admin/grants" element={<GrantAdmin />} />
      </Routes>
    </div>
  );
}